import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthFetch } from '../hooks/useAuthFetch';
import '../stili/AggiornaProgrammaGara.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function AggiornaProgrammaGara() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const eventoIdQuery = query.get('eventoId');

  const [eventi, setEventi] = useState([]);
  const [idEvento, setIdEvento] = useState(eventoIdQuery || '');
  const [eventoNome, setEventoNome] = useState('');
  const eventoSelezionato = useMemo(
    () => eventi.find((e) => String(e.id) === String(idEvento)) || null,
    [eventi, idEvento]
  );

  const [gare, setGare] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [modificaId, setModificaId] = useState(null);

  const [nuovaGara, setNuovaGara] = useState({
    idCategoria: '',
    avversario: '',
    dataGara: '',
    oraGara: '',
    indirizzo: '',
    luogo: '',
    idStaffResponsabile: ''
  });

  const categoriaLabelById = useMemo(() => {
    const m = new Map();
    categorie.forEach((c) => m.set(String(c.id), c.nome));
    return m;
  }, [categorie]);

  const staffLabelById = useMemo(() => {
    const m = new Map();
    staffList.forEach((s) => {
      const label = `${s.cognome || ''} ${s.nome || ''}`.trim();
      m.set(String(s.id), label || s.username || `Staff ${s.id}`);
    });
    return m;
  }, [staffList]);

  useEffect(() => {
    const fetchEventi = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/eventi-gara`);
        if (!res.ok) throw new Error('Errore fetch eventi');
        const data = await res.json();
        setEventi(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(err.message || 'Errore caricamento eventi');
      }
    };

    const fetchCategorie = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/admin/categorie`);
        if (!res.ok) throw new Error('Errore fetch categorie');
        const data = await res.json();
        setCategorie(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(err.message || 'Errore caricamento categorie');
      }
    };

    const fetchStaff = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/admin/lookup/staff`);
        if (!res.ok) throw new Error('Errore fetch staff');
        const data = await res.json();

        if (Array.isArray(data)) {
          setStaffList(data);
          return;
        }

        if (Array.isArray(data?.content)) {
          setStaffList(data.content);
          return;
        }

        setStaffList([]);
      } catch (err) {
        toast.error(err.message || 'Errore caricamento staff');
        setStaffList([]);
      }
    };

    fetchEventi();
    fetchCategorie();
    fetchStaff();
  }, [authFetch]);

  useEffect(() => {
    if (idEvento && eventi.length > 0) {
      const eventoSelezionatoInterno = eventi.find(
        (e) => String(e.id) === String(idEvento)
      );
      setEventoNome(eventoSelezionatoInterno?.nomeEvento || '');
    } else {
      setEventoNome('');
    }
  }, [idEvento, eventi]);

  const fetchGareEvento = useCallback(async () => {
    if (!idEvento) {
      setGare([]);
      return;
    }

    try {
      const res = await authFetch(`${API_URL}/api/gare/evento-gara/${idEvento}`);
      if (!res.ok) throw new Error('Errore fetch gare');

      const data = await res.json();
      setGare(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Errore caricamento gare');
      setGare([]);
    }
  }, [idEvento, authFetch]);

  useEffect(() => {
    fetchGareEvento();
  }, [fetchGareEvento]);

  const aggiornaCampo = (id, campo, valore) => {
    setGare((prev) =>
      prev.map((g) =>
        g.id === id
          ? {
              ...g,
              [campo]: valore
            }
          : g
      )
    );
  };

  const salvaModifiche = async (gara) => {
    try {
      const payload = {
        idEventoGara: Number(idEvento),
        idCategoria: Number(gara.idCategoria),
        avversario: gara.avversario,
        dataGara: gara.dataGara,
        oraGara: gara.oraGara,
        indirizzo: gara.indirizzo,
        luogo: gara.luogo,
        idStaffResponsabile: gara.idStaffResponsabile
          ? Number(gara.idStaffResponsabile)
          : null
      };

      const res = await authFetch(`${API_URL}/api/gare/${gara.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let msg = 'Errore salvataggio';
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      setModificaId(null);
      toast.success('Gara aggiornata con successo');
      fetchGareEvento();
    } catch (err) {
      toast.error(err.message || 'Errore durante il salvataggio');
    }
  };

  const eliminaGara = async (idGara) => {
    toast.dismiss();

    toast.warn(
      ({ closeToast }) => (
        <div style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: 10 }}>
            Sei sicuro di eliminare questa gara?
          </p>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={async () => {
                closeToast();

                try {
                  const res = await authFetch(`${API_URL}/api/gare/${idGara}`, {
                    method: 'DELETE'
                  });

                  if (!res.ok) {
                    let msg = 'Errore eliminazione';
                    try {
                      const err = await res.json();
                      msg = err.message || msg;
                    } catch {}
                    throw new Error(msg);
                  }

                  setGare((prev) => prev.filter((g) => g.id !== idGara));
                  toast.success('Gara eliminata');
                } catch (err) {
                  toast.error(err.message || 'Errore durante l’eliminazione');
                }
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: 'none',
                background: '#ef4444',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Conferma
            </button>

            <button
              onClick={closeToast}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: 'none',
                background: '#6b7280',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Annulla
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false
      }
    );
  };

  const aggiungiGara = async () => {
    if (!idEvento) {
      toast.warning('Seleziona un evento prima di aggiungere una gara');
      return;
    }

    if (
      !nuovaGara.idCategoria ||
      !nuovaGara.avversario ||
      !nuovaGara.dataGara ||
      !nuovaGara.oraGara ||
      !nuovaGara.indirizzo ||
      !nuovaGara.luogo
    ) {
      toast.warning('Compila tutti i campi della nuova gara');
      return;
    }

    try {
      const payload = {
        idEventoGara: Number(idEvento),
        idCategoria: Number(nuovaGara.idCategoria),
        avversario: nuovaGara.avversario,
        dataGara: nuovaGara.dataGara,
        oraGara: nuovaGara.oraGara,
        indirizzo: nuovaGara.indirizzo,
        luogo: nuovaGara.luogo,
        idStaffResponsabile: nuovaGara.idStaffResponsabile
          ? Number(nuovaGara.idStaffResponsabile)
          : null
      };

      const res = await authFetch(`${API_URL}/api/gare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let msg = 'Errore aggiunta gara';
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      toast.success('Gara aggiunta con successo');
      setNuovaGara({
        idCategoria: '',
        avversario: '',
        dataGara: '',
        oraGara: '',
        indirizzo: '',
        luogo: '',
        idStaffResponsabile: ''
      });

      fetchGareEvento();
    } catch (err) {
      toast.error(err.message || 'Errore durante l’aggiunta della gara');
    }
  };

  const scaricaPdf = async () => {
    if (!idEvento) {
      toast.warning('Seleziona un evento prima di scaricare il PDF');
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/eventi-gara/${idEvento}/pdf`);
      if (!response.ok) throw new Error('Errore download PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'programma_gare.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Errore durante il download del PDF');
    }
  };

  const renderDescrizioneGara = (g) => {
    if (g.luogo === 'CASA') {
      return `MONTEROTONDO 1935 vs ${g.avversario}`;
    }

    if (g.luogo === 'TRASFERTA') {
      return `${g.avversario} vs MONTEROTONDO 1935`;
    }

    return g.avversario;
  };

  const aggiornaPubblicazioneBacheca = async (nuovoStato) => {
    if (!eventoSelezionato) {
      toast.warning('Seleziona un evento');
      return;
    }

    try {
      const payload = {
        nomeEvento: eventoSelezionato.nomeEvento,
        idStagione: Number(eventoSelezionato.idStagione),
        visibileBacheca: nuovoStato
      };

      const res = await authFetch(`${API_URL}/api/eventi-gara/${eventoSelezionato.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let msg = 'Errore aggiornamento pubblicazione bacheca';
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      toast.success(
        nuovoStato
          ? 'Programma gare pubblicato in bacheca'
          : 'Programma gare rimosso dalla bacheca'
      );

      const eventiRes = await authFetch(`${API_URL}/api/eventi-gara`);
      if (!eventiRes.ok) throw new Error('Errore aggiornamento elenco eventi');

      const eventiData = await eventiRes.json();
      setEventi(Array.isArray(eventiData) ? eventiData : []);
    } catch (err) {
      toast.error(err.message || 'Errore durante l’aggiornamento della bacheca');
    }
  };

  return (
    <div className="apg-aggiorna-programma-gara-container" translate="no">
      <h2>Aggiorna Programma Gare</h2>

      <div className="apg-label-select-group">
        <label>Seleziona Evento:</label>
        <select value={idEvento} onChange={(e) => setIdEvento(e.target.value)}>
          <option value="">Seleziona Evento</option>
          {eventi.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nomeEvento}
            </option>
          ))}
        </select>
      </div>

      {idEvento && (
        <>
          <h3>
            Programma Gare per Evento: <strong>{eventoNome || 'Evento Selezionato'}</strong>
          </h3>

          <div className="apg-bacheca-actions">
            <span
              className={`apg-bacheca-badge ${
                eventoSelezionato?.visibileBacheca ? 'attiva' : 'non-attiva'
              }`}
            >
              {eventoSelezionato?.visibileBacheca
                ? 'Pubblicato in bacheca'
                : 'Non pubblicato in bacheca'}
            </span>

            <button
              className="apg-button custom-button"
              onClick={() =>
                aggiornaPubblicazioneBacheca(!eventoSelezionato?.visibileBacheca)
              }
            >
              {eventoSelezionato?.visibileBacheca
                ? 'Rimuovi dalla Bacheca'
                : 'Pubblica in Bacheca'}
            </button>
          </div>

          <div className="apg-cards-container">
            {[...gare]
              .sort((a, b) => String(a.idCategoria).localeCompare(String(b.idCategoria)))
              .map((g) => (
                <div className="apg-gara-card" key={g.id}>
                  <div className="apg-gara-header">
                    <div className="apg-gara-title">{renderDescrizioneGara(g)}</div>

                    <div className="apg-gara-badge">
                      {categoriaLabelById.get(String(g.idCategoria)) || '-'}
                    </div>
                  </div>

                  <div className="apg-gara-grid">
                    <div>
                      <span className="apg-label-small">Data</span>
                      <span>
                        {g.dataGara ? new Date(g.dataGara).toLocaleDateString('it-IT') : ''}
                      </span>
                    </div>

                    <div>
                      <span className="apg-label-small">Ora</span>
                      <span>{g.oraGara ? g.oraGara.slice(0, 5) : ''}</span>
                    </div>

                    <div>
                      <span className="apg-label-small">Luogo</span>
                      <span>{g.luogo}</span>
                    </div>

                    <div className="apg-full">
                      <span className="apg-label-small">Indirizzo</span>
                      <span>{g.indirizzo}</span>
                    </div>

                    <div className="apg-full">
                      <span className="apg-label-small">Staff Responsabile</span>
                      <span>
                        {staffLabelById.get(String(g.idStaffResponsabile)) || '-'}
                      </span>
                    </div>
                  </div>

                  <div className="apg-card-actions">
                    <button
                      className="apg-button-modifica"
                      onClick={() => setModificaId(modificaId === g.id ? null : g.id)}
                    >
                      {modificaId === g.id ? 'Chiudi' : 'Modifica'}
                    </button>

                    <button
                      className="apg-button-elimina"
                      onClick={() => eliminaGara(g.id)}
                    >
                      Elimina
                    </button>
                  </div>

                  {modificaId === g.id && (
                    <div className="apg-edit-box">
                      <div className="apg-form-grid">
                        <select
                          value={g.idCategoria || ''}
                          onChange={(e) => aggiornaCampo(g.id, 'idCategoria', e.target.value)}
                        >
                          <option value="">Categoria</option>
                          {categorie.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.nome}
                            </option>
                          ))}
                        </select>

                        <input
                          value={g.avversario || ''}
                          onChange={(e) =>
                            aggiornaCampo(g.id, 'avversario', e.target.value.toUpperCase())
                          }
                        />

                        <input
                          type="date"
                          value={g.dataGara ? g.dataGara.split('T')[0] : ''}
                          onChange={(e) => aggiornaCampo(g.id, 'dataGara', e.target.value)}
                        />

                        <input
                          type="time"
                          value={g.oraGara ? g.oraGara.slice(0, 5) : ''}
                          onChange={(e) => aggiornaCampo(g.id, 'oraGara', e.target.value)}
                        />

                        <input
                          value={g.indirizzo || ''}
                          onChange={(e) =>
                            aggiornaCampo(g.id, 'indirizzo', e.target.value.toUpperCase())
                          }
                        />

                        <select
                          value={g.luogo || ''}
                          onChange={(e) => aggiornaCampo(g.id, 'luogo', e.target.value)}
                        >
                          <option value="">Luogo</option>
                          <option value="CASA">CASA</option>
                          <option value="TRASFERTA">TRASFERTA</option>
                          <option value="NEUTRO">NEUTRO</option>
                        </select>

                        <select
                          value={g.idStaffResponsabile || ''}
                          onChange={(e) =>
                            aggiornaCampo(g.id, 'idStaffResponsabile', e.target.value)
                          }
                        >
                          <option value="">Staff responsabile</option>
                          {staffList.map((s) => (
                            <option key={s.id} value={s.id}>
                              {`${s.cognome || ''} ${s.nome || ''}`.trim() || `Staff ${s.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button className="apg-button" onClick={() => salvaModifiche(g)}>
                        Salva
                      </button>
                    </div>
                  )}
                </div>
              ))}
          </div>

          <div className="apg-nuova-gara-card">
            <h4 className="apg-nuova-gara-title">Aggiungi nuova gara</h4>

            <div className="apg-form-grid">
              <select
                value={nuovaGara.idCategoria}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    idCategoria: e.target.value
                  }))
                }
              >
                <option value="">Categoria</option>
                {categorie.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>

              <input
                value={nuovaGara.avversario}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    avversario: e.target.value.toUpperCase()
                  }))
                }
                placeholder="Squadra avversaria"
              />

              <input
                type="date"
                value={nuovaGara.dataGara}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    dataGara: e.target.value
                  }))
                }
              />

              <input
                type="time"
                value={nuovaGara.oraGara}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    oraGara: e.target.value
                  }))
                }
              />

              <input
                value={nuovaGara.indirizzo}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    indirizzo: e.target.value.toUpperCase()
                  }))
                }
                placeholder="Indirizzo"
              />

              <select
                value={nuovaGara.luogo}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    luogo: e.target.value
                  }))
                }
              >
                <option value="">Luogo</option>
                <option value="CASA">CASA</option>
                <option value="TRASFERTA">TRASFERTA</option>
                <option value="NEUTRO">NEUTRO</option>
              </select>

              <select
                value={nuovaGara.idStaffResponsabile}
                onChange={(e) =>
                  setNuovaGara((prev) => ({
                    ...prev,
                    idStaffResponsabile: e.target.value
                  }))
                }
              >
                <option value="">Staff responsabile</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {`${s.cognome || ''} ${s.nome || ''}`.trim() || `Staff ${s.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="apg-button-row">
              <button className="apg-button custom-button" onClick={aggiungiGara}>
                Aggiungi Gara
              </button>
            </div>
          </div>

          <div className="apg-button-row">
            <button className="apg-button custom-button" onClick={scaricaPdf}>
              Scarica PDF
            </button>

            <button
              className="apg-button custom-button"
              onClick={() => navigate('/gestionale')}
            >
              Torna al Gestionale
            </button>
          </div>
        </>
      )}
    </div>
  );
}